import { useMemo } from 'react';
import useMobxStores from 'hooks/useMobxStores';
import { useTranslation } from 'react-i18next';
import { SubscriptionCode } from 'types';
import { LandingTab } from 'components/Landing/types';

type EmptyStateConfig = {
  message: string;
  buttonLabel?: string;
  buttonTitle?: string;
  onButtonClick?: () => void;
  dataAnalyticsId?: string;
};

const useLiveEventsEmptyState = (): EmptyStateConfig | undefined => {
  const { t } = useTranslation();
  const { routerStore, classesStore, userStore, examsStore } = useMobxStores();

  const { enrolledLiveEvents } = classesStore;
  const {
    currentSubscriptionCode,
    entitlementEnablements,
    isPremiumSubscriber,
    hasExams: hasRemainingExamQuota,
    hasRhlsCourseEnrollment,
    rhlsCourseEnrollments,
  } = userStore;

  const hasUpcomingEvents = !!enrolledLiveEvents?.length;
  const scheduledExams = examsStore?.examStatus?.scheduled ?? [];
  const hasScheduledExams = scheduledExams.length > 0;
  const lowerCaseSubscriptionCode =
    currentSubscriptionCode?.toLowerCase() as SubscriptionCode;

  return useMemo(() => {
    if (hasUpcomingEvents) {
      return undefined;
    }

    // LS520 (Premium) users - always show "Schedule" for courses
    if (lowerCaseSubscriptionCode === SubscriptionCode.Premium) {
      return {
        message: t(
          "No live event scheduled yet. Click the 'Schedule' button to book your class.",
        ),
        buttonLabel: t('Schedule'),
        dataAnalyticsId: 'schedule-btn-home-page-lp',
        onButtonClick: () =>
          routerStore.navigate('catalog', { deliveryFormats: 'course' }),
      };
    }

    // RHLS Course users with no active premium subscription
    // Check this before LS220/LS300 since overlaps should prioritize LVC scheduling
    if (hasRhlsCourseEnrollment && !isPremiumSubscriber) {
      const quotas = entitlementEnablements?.quotas ?? {};

      const hasQuotasExamExperience =
        (quotas?.experiences?.allowed ?? 0) - (quotas?.experiences?.used ?? 0);
      const hasQuotasLiveClass =
        (quotas?.live_class_enrolled?.allowed ?? 0) -
        (quotas?.live_class_enrolled?.used ?? 0);
      const hasQuotasCertificationExams =
        (quotas?.exams?.allowed ?? 0) - (quotas?.exams?.used ?? 0);

      const hasAnyQuotasLeft =
        hasQuotasExamExperience ||
        hasQuotasLiveClass ||
        hasQuotasCertificationExams;

      // All credits exhausted
      if (!hasAnyQuotasLeft) {
        return {
          message: t(
            'You have used all of your available live events for this subscription.',
          ),
        };
      }

      // Has live class credits remaining - show Schedule button
      if (hasQuotasLiveClass) {
        // Users with only one RHLS course enrollment - direct to their course
        if (rhlsCourseEnrollments.length === 1) {
          const offeringSlug = rhlsCourseEnrollments?.[0]?.slug_tag;

          if (offeringSlug) {
            return {
              message: t(
                "No live event scheduled yet. Click the 'Schedule' button to book your class.",
              ),
              buttonLabel: t('Schedule'),
              dataAnalyticsId: 'schedule-btn-home-page-lp',
              buttonTitle: t(
                'Schedule the session for your {{offering}} course',
                {
                  offering: offeringSlug,
                },
              ),
              onButtonClick: () =>
                routerStore.navigate('courses:overview', {
                  course: offeringSlug,
                  tab: LandingTab.LiveClass,
                }),
            };
          }
        }

        // Multiple RHLS courses - go to catalog filtered to their offerings
        return {
          message: t(
            "No live event scheduled yet. Click the 'Schedule' button to book your class.",
          ),
          buttonLabel: t('Schedule'),
          dataAnalyticsId: 'schedule-btn-home-page-lp',
          onButtonClick: () =>
            routerStore.navigate('catalog', { myOfferings: 'visible' }),
        };
      }

      // No live class credits remaining but has other credits
      return {
        message: t(
          'View the Catalog to explore any remaining live events in your subscription.',
        ),
        buttonLabel: t('View catalog'),
        onButtonClick: () =>
          routerStore.navigate('catalog', { myOfferings: 'visible' }),
      };
    }

    const subscriptionCanViewExamSuggestions = [
      SubscriptionCode.DeveloperTier,
      SubscriptionCode.Standard,
    ].includes(lowerCaseSubscriptionCode);

    // LS220/LS300 users with exam suggestions
    if (subscriptionCanViewExamSuggestions && !hasScheduledExams) {
      if (hasRemainingExamQuota) {
        return {
          message: t(
            "No live event scheduled. Click the 'Schedule exam' button to choose an exam to schedule.",
          ),
          buttonLabel: t('Schedule exam'),
          onButtonClick: () =>
            routerStore.navigate('catalog', { deliveryFormats: 'exam' }),
        };
      }
      return {
        message: t(
          'You have used all of your available exams for this subscription.',
        ),
      };
    }

    // Default for Subscription-specific configurations
    const subscriptionConfigs: Partial<
      Record<SubscriptionCode, { buttonText: string; onContinue: () => void }>
    > = {
      [SubscriptionCode.LimitedTrial]: {
        buttonText: t('Exam experience'),
        onContinue: () =>
          routerStore.navigate('catalog', { deliveryFormats: 'experience' }),
      },
      [SubscriptionCode.WorkForceDevelopmentTier]: {
        buttonText: t('Certification Exams'),
        onContinue: () =>
          routerStore.navigate('catalog', { deliveryFormats: 'exam' }),
      },
    };

    const subscriptionConfig = subscriptionConfigs[lowerCaseSubscriptionCode];
    const buttonText = subscriptionConfig?.buttonText || t('View catalog');

    return {
      message: t(
        "No live event scheduled yet. Click the '{{buttonText}}' button to book your class.",
        { buttonText },
      ),
      buttonLabel: buttonText,
      onButtonClick:
        subscriptionConfig?.onContinue ||
        (() => routerStore.navigate('catalog')),
    };
  }, [
    hasUpcomingEvents,
    isPremiumSubscriber,
    entitlementEnablements,

    hasScheduledExams,
    hasRemainingExamQuota,
    hasRhlsCourseEnrollment,
    rhlsCourseEnrollments,
    lowerCaseSubscriptionCode,
    routerStore,
    t,
  ]);
};

export default useLiveEventsEmptyState;
